import React, { useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { reduxForm, Field, getFormValues } from 'redux-form';
import { Form } from 'semantic-ui-react';
import {
  renderDropdown,
  hideModal,
  createLoadingSelector,
} from 'erisxkit/client';
import get from 'lodash/get';
import unset from 'lodash/unset';
import GenericModal from '../../../common/GenericModal';
import { ApprovedParticipantAction } from './Action/ParticipantActions';
import {
  FETCH_CGM_LIST,
  fetchCgmList,
  getCgmNumberSelectOptions,
  linkCgmToParticipantPromise,
  unlinkCgmToParticipantPromise,
} from '../../../reducers/cgmManagementReducer';
import {
  GET_TRADING_PARTICIPANTS,
  fetchTradingParticipants,
  getParticipantCGMLinkModalLoading,
  getParticipantListSelectOptions,
} from '../../../reducers/participantManagementReducer';
import {
  CGMRow,
  TradingParticipant,
} from '../../../ts/models/TradingParticipant/ApprovedParticipant.model';
import ModalDetails, { InfoField } from './ModalDetails';
import { RootState } from '../../../ts/types/store';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { getCustomerAccountFromMember } from '../../../reducers/accountsReducer';
import Account from '../../../ts/models/Account.model';

const StyledForm = styled(Form)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 15px;
`;

type IForm = {
  isLink: boolean;
  participant?: TradingParticipant;
  accountLabel?: string;
};
type IProps = {
  isLink: boolean;
  participant?: TradingParticipant;
  accountLabel?: string;
};

const LINK_FORM_NAME = 'participantLinkCGM';
const ParticipantCGMLinkForm = reduxForm<IForm, IProps>({
  form: LINK_FORM_NAME,
})(({ isLink, participant, change, accountLabel }) => {
  const dispatch = useAppDispatch();
  const cgmOptions = useAppSelector(getCgmNumberSelectOptions);
  const participantOptions = useAppSelector(getParticipantListSelectOptions);
  const loading = useAppSelector(getParticipantCGMLinkModalLoading);
  const loadingCGMs = useAppSelector((state: RootState) =>
    createLoadingSelector([FETCH_CGM_LIST])(state),
  );
  const loadingParticipants = useAppSelector((state: RootState) =>
    createLoadingSelector([GET_TRADING_PARTICIPANTS])(state),
  );

  useEffect(() => {
    dispatch(
      fetchTradingParticipants({
        urlParams: { accountLabel },
      }),
    );
  }, [accountLabel, dispatch]);

  useEffect(() => {
    // If action is link and a participant was passed,
    // initialize participant dropdown
    if (participant) {
      // Delete subrows key because that throws off redux-form equality check
      unset(participant, 'subRows');
      change('participant', participant);
    }
  }, [change, isLink, participant]);

  return (
    <StyledForm>
      <Field
        component={renderDropdown}
        placeholder="Enter name"
        name="cgmName"
        id="cgmName"
        type="text"
        required
        label="CGM"
        fluid
        options={cgmOptions}
        disabled={loading}
        search
        loading={loadingCGMs}
      />
      <Field
        component={renderDropdown}
        label="Participant"
        fluid
        required
        placeholder="Select"
        name="participant"
        id="participant"
        options={participantOptions}
        disabled={loading}
        search
        loading={loadingParticipants}
      />
    </StyledForm>
  );
});

const ParticipantCGMLinkModal: React.FC<{
  action: ApprovedParticipantAction;
  cgm: CGMRow;
  participant: TradingParticipant;
  memberId: string;
  fetchData: () => void;
}> = ({ action, cgm, participant, memberId, fetchData }) => {
  const dispatch = useAppDispatch();
  const loading = useAppSelector(getParticipantCGMLinkModalLoading);
  const formValues = useAppSelector((state: RootState) =>
    getFormValues(LINK_FORM_NAME)(state),
  );
  const account: Account = useAppSelector((state: RootState) =>
    getCustomerAccountFromMember(state, memberId),
  );
  const accountLabel = account?.label;
  const isLink = action === ApprovedParticipantAction.LINK;
  const header = `${isLink ? 'Link' : 'Un-Link'} CGM to Participant`;
  const cgmNumber = get(formValues, 'cgmName', '');
  const participantToLink: TradingParticipant = get(
    formValues,
    'participant',
    '',
  );

  useEffect(() => {
    if (isLink)
      dispatch(
        fetchCgmList({
          urlParams: { accountLabel },
        }),
      );
  }, [dispatch, isLink, accountLabel]);

  const unlinkFields = useMemo(() => {
    return [
      {
        header: 'Trading Participant',
        text: participant?.participantName || '',
      },
      { header: 'CGM', text: cgm?.cgmName || '' },
    ] as InfoField[];
  }, [cgm, participant]);

  const content = useMemo(() => {
    return isLink ? (
      <ParticipantCGMLinkForm
        accountLabel={accountLabel}
        isLink={isLink}
        participant={participant}
      />
    ) : (
      <ModalDetails
        fields={unlinkFields}
        subHeader="You are about to Unlink a CGM"
      />
    );
  }, [isLink, participant, unlinkFields]);

  const onLink = useCallback(async () => {
    try {
      await linkCgmToParticipantPromise(
        {
          tradingFirmMemberId: participantToLink.participantId,
          accountLabel,
          cgmNumber,
          urlParams: {
            accountLabel,
            cgmNumber,
          },
        },
        dispatch,
      );
      dispatch(hideModal());
      fetchData();
    } catch (error) {
      dispatch(hideModal());
    }
  }, [
    accountLabel,
    cgmNumber,
    dispatch,
    participantToLink.participantId,
    fetchData,
  ]);

  const onUnlink = useCallback(async () => {
    try {
      await unlinkCgmToParticipantPromise(
        {
          tradingFirmMemberId: participant?.participantId,
          accountLabel,
          cgmNumber: cgm.cgmName,
          urlParams: {
            accountLabel,
            cgmNumber: cgm.cgmName,
          },
        },
        dispatch,
      );
      dispatch(hideModal());
      fetchData();
    } catch (error) {
      dispatch(hideModal());
    }
  }, [accountLabel, cgm, dispatch, participant?.participantId, fetchData]);

  const linkButtonDisabled = useMemo(
    () => loading || !participantToLink || !cgmNumber,
    [cgmNumber, loading, participantToLink],
  );

  const confirmProps = useMemo(
    () => ({
      text: isLink ? 'Link' : 'Unlink',
      onClick: isLink ? onLink : onUnlink,
      danger: !isLink,
      disabled: isLink ? linkButtonDisabled : false,
    }),
    [isLink, linkButtonDisabled, onLink, onUnlink],
  );

  const cancelProps = useMemo(
    () => ({
      text: 'Cancel',
      onClick: () => dispatch(hideModal()),
    }),
    [dispatch],
  );

  return (
    <GenericModal
      header={header}
      content={content}
      cancelProps={cancelProps}
      confirmProps={confirmProps}
      loadingSelector={getParticipantCGMLinkModalLoading}
    />
  );
};

export default ParticipantCGMLinkModal;
